import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, kaspreadsheetUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm border-b border-background-200/70">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
              gtbuyspreadsheet<span className="text-primary-500">.team</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3">
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/shopping-guide"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/shopping-guide') || location.pathname.startsWith('/shopping-guide')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              <i className="ri-book-open-line w-4 h-4 flex items-center justify-center inline mr-1.5"></i>
              Shopping Guide
            </Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-200/60 text-foreground-700 text-sm font-medium hover:border-foreground-400 transition-colors cursor-pointer whitespace-nowrap bg-background-50"
              >
                <i className="ri-store-2-line w-4 h-4 flex items-center justify-center"></i>
                <span>All Categories</span>
                <i className={`w-4 h-4 flex items-center justify-center transition-transform ${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shopping-bag-3-line text-foreground-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop CTA */}
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              gtbysheet
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          {/* Quick Nav */}
          <div className="px-4 py-4 border-b border-background-100">
            <div className="flex items-center gap-2">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex-1 text-center px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                  isActive('/') ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                }`}
              >
                <i className="ri-home-4-line w-4 h-4 flex items-center justify-center mx-auto mb-1"></i>
                Home
              </Link>
              <Link
                to="/shopping-guide"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex-1 text-center px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                  isActive('/shopping-guide') || location.pathname.startsWith('/shopping-guide')
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                }`}
              >
                <i className="ri-book-open-line w-4 h-4 flex items-center justify-center mx-auto mb-1"></i>
                Shopping Guide
              </Link>
            </div>
          </div>

          {/* Categories Toggle */}
          <div className="px-4 py-3">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-950 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-store-2-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                All Categories
              </span>
              <i className={`w-4 h-4 flex items-center justify-center transition-transform ${mobileCatOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-up-line text-foreground-400 text-xs w-3 h-3 flex items-center justify-center"></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Mobile CTA */}
          <div className="px-4 pb-5 pt-2">
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              gtbysheet — Shop Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}